/**
 * MigrationWelcomeModal - One-time onboarding for existing users
 * Shows when user has been migrated to multi-project support
 * SoW-121: G1-F7 - Migration Welcome Modal
 */

import React, { useState, useEffect } from 'react';
import { Modal } from '../shared/Modal';
import {
  SparklesIcon,
  FolderIcon,
  DocumentDuplicateIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';

interface MigrationWelcomeModalProps {
  /** Force show modal (for testing) */
  forceShow?: boolean;

  /** Callback when modal is dismissed */
  onDismiss?: () => void;
}

const MIGRATION_MODAL_KEY = 'fmeastudio_migration_modal_seen';

/**
 * Safe localStorage access — private-browsing / SSR / disabled-storage can make
 * localStorage throw. Reading must never crash the mount effect, and a failed
 * write must be caught so dismissal state is handled gracefully.
 */
function hasSeenMigrationModal(): boolean {
  try {
    return localStorage.getItem(MIGRATION_MODAL_KEY) === 'true';
  } catch {
    // If we can't read storage, assume seen so we don't nag on every load.
    return true;
  }
}

function markMigrationModalSeen(): void {
  try {
    localStorage.setItem(MIGRATION_MODAL_KEY, 'true');
  } catch {
    // Best-effort persistence; ignore storage failures.
  }
}

/**
 * Onboarding modals should never block automated (E2E / crawler) sessions, which
 * always start with empty storage and would otherwise render a full-viewport
 * scrim on every route. Matches the existing `!navigator.webdriver` convention
 * used for the React Query devtools in App.tsx.
 */
function isAutomatedSession(): boolean {
  return typeof navigator !== 'undefined' && navigator.webdriver === true;
}

export function MigrationWelcomeModal({
  forceShow = false,
  onDismiss,
}: MigrationWelcomeModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Never show onboarding to automation (unless a test explicitly forces it).
    if (!forceShow && isAutomatedSession()) {
      return;
    }

    // Show once: skip if the user has already dismissed it (persisted).
    if (forceShow || !hasSeenMigrationModal()) {
      // Small delay for smoother UX
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  const handleClose = () => {
    setIsOpen(false);

    // Persist dismissal so the modal only ever shows once per browser.
    markMigrationModalSeen();

    // Call optional callback
    onDismiss?.();
  };

  const features = [
    {
      icon: <FolderIcon className="w-6 h-6 text-blue-500" />,
      title: 'Multiple Projects',
      description: 'Work on multiple FMEAs simultaneously without mixing data',
    },
    {
      icon: <DocumentDuplicateIcon className="w-6 h-6 text-green-500" />,
      title: 'Copy & Reuse',
      description: 'Duplicate existing FMEAs as starting points for new projects',
    },
    {
      icon: <ArchiveBoxIcon className="w-6 h-6 text-purple-500" />,
      title: 'Archive Completed Work',
      description: 'Keep your workspace clean by archiving finished FMEAs',
    },
  ];

  const footer = (
    <button
      type="button"
      onClick={handleClose}
      className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
      data-testid="migration-modal-got-it"
    >
      Got it, let's go!
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      size="lg"
      footer={footer}
      showCloseButton={true}
      preventClose={false}
    >
      {/* Height-cap the scrollable body so tall content scrolls internally and
          the footer CTA ("Got it, let's go!") is always visible in the viewport,
          even on short screens. */}
      <div
        className="text-center"
        data-testid="migration-welcome-modal"
        style={{ maxHeight: '65vh', overflowY: 'auto' }}
      >
        {/* Icon */}
        <div className="mx-auto flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <SparklesIcon className="w-8 h-8 text-blue-600" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Welcome to Multi-Project Support! 🎉
        </h2>

        {/* Description */}
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          We've upgraded FMEAstudio with powerful new project management features. Your existing
          work has been automatically migrated to a default project.
        </p>

        {/* Features */}
        <div className="space-y-4 mb-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg text-left"
              data-testid={`migration-feature-${index}`}
            >
              <div className="flex-shrink-0 mt-0.5">{feature.icon}</div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* What Changed */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <span className="text-blue-600">ℹ️</span>
            What changed?
          </h4>
          <ul
            className="text-sm text-blue-800 space-y-1"
            style={{ listStyle: 'disc', paddingLeft: '1.25rem', margin: 0 }}
          >
            <li>All your existing data is now in a project called &quot;Default Project&quot;</li>
            <li>Use the project switcher in the top bar to create new projects</li>
            <li>Each project has its own components, failures, and data</li>
            <li>The FMEA wizard can now help you create projects from templates</li>
          </ul>
        </div>

        {/* Optional: Quick Start */}
        <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
          <p className="text-sm text-gray-700">
            <strong>Quick Start:</strong> Click the <strong>+ New Project</strong> button in the top
            bar to create your first multi-project FMEA!
          </p>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Hook to programmatically show the migration modal
 */
export function useMigrationModal() {
  const [shouldShow, setShouldShow] = useState(false);

  const show = () => setShouldShow(true);
  const hide = () => setShouldShow(false);

  const hasSeenModal = () => hasSeenMigrationModal();

  const resetModal = () => {
    try {
      localStorage.removeItem(MIGRATION_MODAL_KEY);
    } catch {
      // ignore storage failures
    }
  };

  return {
    show,
    hide,
    shouldShow,
    hasSeenModal,
    resetModal,
  };
}
